/* Photo report review modal — one report at a time, stepped through the caller's queue.
   Visual reference: docs/design/photo-review.html (`.modal` / `.stage` / `.side-info`).

   Archetype C in docs/design/spec/03-patterns.md: a queue plus one document, keyboard-driven.
   ← → step the queue, A approves, R rejects, Esc closes; the thumbnail strip steps the photos
   of the current report. antd's Modal supplies the mask, the focus trap and Esc.

   SHARED by /photo-review and /visit/:id's reports section — both list the same
   `photos_in_visit` / `photos_from_reports` records, and the visit page was opening a generic
   `<Descriptions>` dialog for them instead: no zoom, no thumbnail strip, no keyboard. Styles are
   `component/review-modal.scss`, imported once from `src/index.js`.

   ⚠ `item` must be a `PhotoReview` (`src/models/photo-review.js`), not a raw API row. `photo` is
   an ARRAY that only that model synthesises — the endpoints carry `url_photo` / `ticket_contents`,
   and reading `item.photo` off a raw row is why the visit page's report covers were all
   placeholders. */

import React, { useEffect, useState } from "react";
import { Button, Col, Descriptions, Divider, Flex, Image, Modal, Row, Skeleton, Space, Tag, Tooltip, Typography } from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  CompressOutlined,
  FileImageOutlined,
  LeftOutlined,
  LinkOutlined,
  RightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from "@ant-design/icons";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";

import { ENTITY_DROPDOWN_SETTINGS, PHOTO_REPORT, PHOTO_REVIEW } from "../config/lang";
import { call } from "../util/action";
import { localeFor } from "../module/merchandiser/route/dateUtils";
import { translate } from "../util/util";

/* The photo report's own small pieces stay where the concept lives; `parts.js` imports no
   stylesheet, so this costs nothing but the path — unlike importing a page's SCSS. */
import { StatusPill, fill, fullName, statusOf } from "../module/photo-review/parts";

/**
 * The rail's label/value groups: antd `<Descriptions>`, per spec/02 ("Key/value read-only →
 * `<Descriptions>`", "Detail modal → `<Modal>` + `<Descriptions>`").
 *
 * `vertical` is the design's stacked pair. Rows with no value are dropped BEFORE the items array
 * is handed over — `Descriptions` renders whatever it is given, so an absent brand or price would
 * otherwise show as a label above an empty line. That filtering is the one behaviour the old
 * hand-built `Kv` had that antd does not.
 */
function Facts({ rows }) {
  const items = rows
    .filter(row => row && row.children !== null && row.children !== undefined && row.children !== "" && row.children !== false)
    .map((row, i) => ({ key: String(i), ...row }));
  if (!items.length) return null;
  return <Descriptions className="pr-facts" column={1} size="small" layout="vertical" colon={false} items={items} />;
}

/** Mounted only while a report is open, so `item` is always present. */
export default function ReviewModal({ item, position, total, lang, busy, onClose, onPrev, onNext, onApprove, onReject }) {
  const t = (key, group = PHOTO_REPORT) => translate(group, key, lang);
  const [photoIndex, setPhotoIndex] = useState(0);
  /* The linked task lives on a different record; fetched only when this modal is open and only
     for answer-type reports, per the hydrate-on-demand rule (spec/03). */
  const [answer, setAnswer] = useState(null);
  const [answerLoading, setAnswerLoading] = useState(false);

  const photos = item.photo || [];
  const status = statusOf(item);
  const info = item.additional_info || {};
  const point = item.visit?.point;
  /* Pulled out as plain locals so the hook dependencies below are statically checkable. */
  const itemId = item.id;
  const objectId = item.picsell_info?.object_id;
  const objectType = item.picsell_info?.object_type;
  const dateTime = value => (value ? new Intl.DateTimeFormat(localeFor(lang), { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "");

  useEffect(() => setPhotoIndex(0), [itemId]);

  useEffect(() => {
    if (objectType !== "answer" || !objectId) {
      setAnswer(null);
      return;
    }
    setAnswer(null);
    setAnswerLoading(true);
    call({ method: "GET", url: `answers/${objectId}/` }, data => {
      setAnswer(data && data.task ? data : null);
      setAnswerLoading(false);
    });
  }, [objectId, objectType]);

  /* Keyboard drives the queue. Bound on the document rather than the dialog so it works
     wherever focus happens to sit inside the modal; Esc is antd's own. */
  useEffect(() => {
    const onKey = event => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const tag = document.activeElement?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (event.key === "ArrowRight") onNext();
      else if (event.key === "ArrowLeft") onPrev();
      else if (event.key === "a" || event.key === "A") onApprove();
      else if (event.key === "r" || event.key === "R") onReject();
      else return;
      event.preventDefault();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onNext, onPrev, onApprove, onReject]);

  const visitStart = dateTime(item.visit?.start);
  const visitEnd = dateTime(item.visit?.end);
  const approvedAt = dateTime(item.approval?.approved_at);

  const tags = Array.isArray(info.tags) ? info.tags : typeof info.tags === "string" && info.tags ? [info.tags] : [];

  const title = (
    <Flex className="pr-modal__head" align="center" gap="middle">
      <Typography.Title level={4} className="pr-modal__title">{`${t("review_title")} · #${item.id}`}</Typography.Title>
      <Typography.Text type="secondary" className="pr-modal__pos">
        {fill(t("w_position"), { i: position, n: total })}
      </Typography.Text>
      <Space className="pr-modal__nav">
        <Button size="small" icon={<LeftOutlined />} aria-label={t("a_prev")} onClick={onPrev} />
        <Button size="small" icon={<RightOutlined />} aria-label={t("a_next")} onClick={onNext} />
        <Button size="small" type="text" icon={<CloseOutlined />} aria-label={t("a_close")} onClick={onClose} />
      </Space>
    </Flex>
  );

  const footer = (
    <Flex className="pr-modal__foot" align="center" gap="small">
      <Typography.Text type="secondary" className="pr-modal__hint">
        {t("kbd_hint")}
      </Typography.Text>
      <Space className="pr-modal__acts">
        <Button className="pr-btn--no" icon={<CloseCircleOutlined />} loading={busy} disabled={status === "rejected"} onClick={onReject}>
          {translate(PHOTO_REVIEW, "reject_btn", lang)}
        </Button>
        <Button className="pr-btn--ok" type="primary" icon={<CheckCircleOutlined />} loading={busy} disabled={status === "approved"} onClick={onApprove}>
          {translate(PHOTO_REVIEW, "approv_btn", lang)}
        </Button>
      </Space>
    </Flex>
  );

  return (
    <Modal
      open
      /* The head carries prev / next / close together, so antd's own close button would be a
         fourth control in the same corner. */
      closable={false}
      title={title}
      footer={footer}
      width={1120}
      centered
      rootClassName="pr-modal"
      onCancel={onClose}>
      <Row className="pr-modal__body" wrap={false}>
        <Col className="pr-stage" flex="auto">
          <Flex className="pr-stage__img" align="center" justify="center">
            {photos.length ? (
              /* Zoom happens IN the stage — antd's `Image` preview would have given wheel zoom and
                 pan for free, but only by opening a full-screen overlay above this modal, which
                 hides the facts rail the reviewer is checking the photo against.
                 `react-zoom-pan-pinch` is the wheel/drag/pinch surface antd has no equivalent for.
                 Keyed on the photo so switching one resets the transform — a 6× zoom carried over
                 to the next shelf is disorienting. */
              <TransformWrapper
                key={`${itemId}-${photoIndex}`}
                minScale={1}
                maxScale={8}
                doubleClick={{ mode: "reset" }}
                wheel={{ step: 0.2 }}
                /* The stage is bigger than the letterboxed photo, and that dark margin is part of
                   the working area: `limitToBounds` would stop the drag at the photo's own edge,
                   so a corner of a zoomed shelf could never be brought to the middle of the view.
                   `centerZoomedOut` puts it back when you zoom out to fit, so free panning cannot
                   strand the photo off-screen. */
                limitToBounds={false}
                centerZoomedOut
                centerOnInit
                panning={{ velocityDisabled: true }}>
                {({ zoomIn, zoomOut, resetTransform }) => (
                  <>
                    <TransformComponent wrapperClass="pr-zoom" contentClass="pr-zoom__content">
                      {/* `draggable={false}` is what makes panning work at all: a browser starts
                          its own drag-and-drop on an <img>, which takes the pointer before the
                          pan handler ever sees a move. */}
                      <Image src={photos[Math.min(photoIndex, photos.length - 1)]} alt="" preview={false} draggable={false} rootClassName="pr-stage__imgwrap" />
                    </TransformComponent>
                    {/* The wheel is the primary control; these are for trackpads that swallow it
                        and for anyone who cannot use a pointer gesture at all. */}
                    {/* Plain default `<Button>`s, not the cover's `.pr-navbtn`: that one is a
                        borderless translucent puck built to sit on a photo, and it disappears on
                        the white canvas. A default button is already the right thing here. */}
                    <Space className="pr-zoom__acts" orientation="vertical" size={4}>
                      <Button shape="circle" size="small" icon={<ZoomInOutlined />} aria-label={t("a_zoom_in")} onClick={() => zoomIn()} />
                      <Button shape="circle" size="small" icon={<ZoomOutOutlined />} aria-label={t("a_zoom_out")} onClick={() => zoomOut()} />
                      <Button shape="circle" size="small" icon={<CompressOutlined />} aria-label={t("a_zoom_reset")} onClick={() => resetTransform()} />
                    </Space>
                  </>
                )}
              </TransformWrapper>
            ) : (
              <Flex className="pr-stage__none" vertical align="center" gap="small">
                <FileImageOutlined className="pr-stage__none-icon" />
                {t("no_photo")}
              </Flex>
            )}
          </Flex>
          {photos.length > 1 && (
            <Flex className="pr-strip" gap="small">
              {photos.map((src, i) => (
                <Button
                  key={`${src}-${i}`}
                  className="pr-thumb"
                  aria-current={i === photoIndex}
                  aria-label={fill(t("a_photo_n"), { n: i + 1 })}
                  onClick={() => setPhotoIndex(i)}>
                  <Image src={src} alt="" preview={false} rootClassName="pr-thumb__imgwrap" />
                </Button>
              ))}
            </Flex>
          )}
        </Col>

        <Col className="pr-rail" flex="0 0 330px">
          <StatusPill status={status} lang={lang} inline />

          <Facts
            rows={[
              {
                label: translate(ENTITY_DROPDOWN_SETTINGS, "point", lang),
                children: point?.name ? (
                  <>
                    {point.name}
                    <Typography.Text type="secondary" className="pr-facts__sub">
                      {[point.locality?.name, point.network?.name].filter(Boolean).join(" · ")}
                    </Typography.Text>
                  </>
                ) : null,
              },
              { label: translate(PHOTO_REVIEW, "merchendiser", lang), children: fullName(item?.visit?.user) },
              { label: translate(PHOTO_REVIEW, "responsible_supervisor", lang), children: fullName(point?.responsible_supervisors?.[0]) },
              { label: translate(PHOTO_REVIEW, "start_visit", lang), children: visitStart },
              { label: translate(PHOTO_REVIEW, "end_visit", lang), children: visitEnd },
            ]}
          />

          <Divider />

          {/* Brand / category / client only exist on records the recogniser tagged — the row
              drops out entirely when the field is absent rather than rendering an empty label. */}
          <Facts
            rows={[
              { label: translate(ENTITY_DROPDOWN_SETTINGS, "brand", lang), children: info.brand },
              { label: translate(ENTITY_DROPDOWN_SETTINGS, "category", lang), children: info.category },
              { label: translate(ENTITY_DROPDOWN_SETTINGS, "clients", lang), children: info.client },
              {
                label: translate(ENTITY_DROPDOWN_SETTINGS, "tags", lang),
                children: tags.length > 0 && (
                  <Flex gap={4} wrap>
                    {tags.map(tag => (
                      <Tag variant="filled" className="pr-tag" key={tag}>
                        {tag}
                      </Tag>
                    ))}
                  </Flex>
                ),
              },
            ]}
          />

          {(info.price || info.sale_price) && info.price !== "None" ? (
            <>
              <Divider />
              <Facts
                rows={[
                  { label: translate(PHOTO_REVIEW, "price", lang), children: info.price !== "None" ? info.price : null },
                  { label: translate(PHOTO_REVIEW, "sales_price", lang), children: info.sale_price !== "None" ? info.sale_price : null },
                ]}
              />
            </>
          ) : null}

          {info.comment ? (
            <>
              <Divider />
              <Facts rows={[{ label: translate(PHOTO_REVIEW, "comment", lang), children: info.comment }]} />
            </>
          ) : null}

          <Divider />
          <Facts
            rows={[
              {
                label: translate(PHOTO_REVIEW, "linkedVisit", lang),
                children: item.visit_id ? (
                  <Typography.Link href={`/visit/${item.visit_id}`} target="_blank" rel="noopener noreferrer">
                    <LinkOutlined className="pr-facts__link-icon" /> {[point?.locality?.name, point?.name].filter(Boolean).join(" ")}
                  </Typography.Link>
                ) : null,
              },
              {
                label: translate(PHOTO_REVIEW, "linkedTasks", lang),
                children: answer ? (
                  <Typography.Link href={`/task/${answer.task.id}`} target="_blank" rel="noopener noreferrer">
                    <LinkOutlined className="pr-facts__link-icon" /> #{answer.task.id} — {answer.task.title}
                  </Typography.Link>
                ) : null,
              },
            ]}
          />
          {answerLoading && <Skeleton active paragraph={{ rows: 2 }} title={false} />}

          {item.approval?.approved_at ? (
            <>
              <Divider />
              <Facts
                rows={[
                  {
                    label: translate(PHOTO_REVIEW, "approve_reject_label_block", lang),
                    children: (
                      <Tooltip title={approvedAt}>
                        <Typography.Text>
                          {fullName(item.approval.approved_by)} · {approvedAt}
                        </Typography.Text>
                      </Tooltip>
                    ),
                  },
                ]}
              />
            </>
          ) : null}
        </Col>
      </Row>
    </Modal>
  );
}
